// Per-page coach mark step definitions.
//
// Keyed by router `page` id. Each entry is an array of steps that
// match the CoachMark shape: { target, title, body, placement?,
// accent? }. The target is a CSS selector pointing at a
// [data-coach="..."] attribute on the page's key controls.
//
// New pages: add data-coach attributes to the relevant elements,
// then add an entry here. The usePageCoachTour hook handles
// auto-launch and persistence per user + per page.
//
// Pages live across roles — landlord-dash differs from tenant-dash,
// so we key on role + page id ("dash_landlord", "dash_tenant", …).

const PAGE_COACH_STEPS = {
  // ───── Landlord pages ─────────────────────────────────

  dash_landlord: [
    {
      target: '[data-coach="dash-stats"]',
      title: "Your stats at a glance",
      body: "Total properties, occupied units, rent collected this month — the headline numbers for your business.",
      accent: "ac",
    },
    {
      target: '[data-coach="dash-quick-actions"]',
      title: "Quick Actions",
      body: "Jump to the most common tasks: add a property, invite a tenant, view reports. Expand or collapse this list anytime.",
      accent: "gn",
    },
    {
      target: '[data-coach="dash-recent"]',
      title: "Recent activity",
      body: "A log of payments, maintenance updates, and tenant changes — keeps you informed without checking every page.",
      accent: "pp",
    },
  ],

  props: [
    {
      target: '[data-coach="props-add"]',
      title: "Add a property",
      body: "Tap here to register a new rental — set rent, deposit, amenities, and upload photos.",
      accent: "ac",
    },
    {
      target: '[data-coach="props-list"]',
      title: "Your properties",
      body: "Each card shows occupancy, current tenant, and lets you edit, view, or mark a property as vacant.",
      accent: "gn",
    },
  ],

  tens: [
    {
      target: '[data-coach="tens-invite"]',
      title: "Invite a tenant",
      body: "Send a WhatsApp invite — your tenant can log in, pay rent, and chat with you directly.",
      accent: "ac",
    },
    {
      target: '[data-coach="tens-list"]',
      title: "Your tenants",
      body: "Tap any tenant to see their lease, payment history, and chat. Filter by property or status.",
      accent: "gn",
    },
  ],

  pay: [
    {
      target: '[data-coach="pay-summary"]',
      title: "Filter & summary",
      body: "Toggle between pending, paid, due, and advance tenants. The list below updates instantly.",
      accent: "ac",
    },
    {
      target: '[data-coach="pay-add"]',
      title: "Record a payment",
      body: "Tap here when a tenant pays offline (cash, bank transfer) so the books stay in sync.",
      accent: "gn",
    },
  ],

  maint: [
    {
      target: '[data-coach="maint-list"]',
      title: "Maintenance requests",
      body: "Tenants raise requests with photos. Assign a service person, track progress, approve their payment.",
      accent: "ac",
    },
    {
      target: '[data-coach="maint-add"]',
      title: "Create a request",
      body: "Add a maintenance task yourself — useful for proactive work like annual inspections or repainting.",
      accent: "gn",
    },
  ],

  vacateInbox: [
    {
      target: '[data-coach="vacate-list"]',
      title: "Vacate requests",
      body: "Tenants who've requested to move out. Review their notice period, settle the deposit, and close the lease.",
      accent: "wn",
    },
  ],

  agreement: [
    {
      target: '[data-coach="agreement-list"]',
      title: "Rental agreements",
      body: "All your lease agreements with tenants — generate new ones, e-sign, and download PDFs.",
      accent: "ac",
    },
  ],

  servicePersons: [
    {
      target: '[data-coach="sp-add"]',
      title: "Add a service person",
      body: "Build your roster of plumbers, electricians, and cleaners. Assign them to jobs from the Maintenance tab.",
      accent: "ac",
    },
    {
      target: '[data-coach="sp-list"]',
      title: "Your service team",
      body: "Tap anyone to see their skills, completed jobs, and pending payouts.",
      accent: "gn",
    },
  ],

  inviteTenant: [
    {
      target: '[data-coach="invite-form"]',
      title: "Send a tenant invite",
      body: "Fill in their phone + which property. They get a WhatsApp link to set their password and log in.",
      accent: "ac",
    },
  ],

  leads: [
    {
      target: '[data-coach="leads-list"]',
      title: "Prospective tenants",
      body: "People who inquired about your listed properties on the public marketplace. Reply via WhatsApp or convert to a tenant.",
      accent: "ac",
    },
  ],

  paySettings: [
    {
      target: '[data-coach="paysettings-upi"]',
      title: "Auto Rent Reminders",
      body: "Toggle on to automatically send WhatsApp reminders before rent is due. Cuts your follow-ups in half.",
      accent: "ac",
    },
    {
      target: '[data-coach="paysettings-razorpay"]',
      title: "WhatsApp Bot",
      body: "Let tenants check their balance and recent payments by messaging your number — even at 2am.",
      accent: "gn",
    },
  ],

  team: [
    {
      target: '[data-coach="team-invite"]',
      title: "Invite a teammate",
      body: "Add an accountant or property manager to help run things. You control what they can see and do.",
      accent: "ac",
    },
    {
      target: '[data-coach="team-list"]',
      title: "Team members",
      body: "Every person who can log into your org. Tap anyone to adjust their role or remove access.",
      accent: "gn",
    },
  ],

  faq: [
    {
      target: '[data-coach="faq-search"]',
      title: "Rent act guide",
      body: "State-aware tenancy law cheat sheet — security deposits, eviction rules, rent caps. Tap any section to expand.",
      accent: "ac",
    },
  ],

  reports: [
    {
      target: '[data-coach="reports-period"]',
      title: "Headline numbers",
      body: "Collected this period, what's still pending, and occupancy — your three most-checked stats.",
      accent: "ac",
    },
    {
      target: '[data-coach="reports-export"]',
      title: "Generate a report",
      body: "Tap any row to build a report — payments, leases, tenants, or maintenance. Export to CSV on paid plans.",
      accent: "gn",
    },
  ],

  // ───── Tenant pages ───────────────────────────────────

  dash_tenant: [
    {
      target: '[data-coach="tendash-rent"]',
      title: "Your current rent",
      body: "Due date, amount, and a one-tap Pay button right where you need it.",
      accent: "ac",
    },
    {
      target: '[data-coach="tendash-prop"]',
      title: "Your home",
      body: "Property details, landlord contact, and your lease terms — all in one place.",
      accent: "gn",
    },
  ],

  // ───── Service-person pages ──────────────────────────

  dash_service: [
    {
      target: '[data-coach="spdash-active"]',
      title: "Active jobs",
      body: "Maintenance requests assigned to you. Tap any to update status or add photos.",
      accent: "ac",
    },
    {
      target: '[data-coach="spdash-earnings"]',
      title: "This month's earnings",
      body: "Total paid + pending. Tap through to see the breakdown by job.",
      accent: "gn",
    },
  ],
};

// Helper: resolve the steps for the current page+role combination.
// Falls back to the bare page id when no role variant exists.
export function getPageCoachSteps(page, role) {
  if (!page) return [];
  const roleKey = role ? `${page}_${role}` : null;
  if (roleKey && PAGE_COACH_STEPS[roleKey]) return PAGE_COACH_STEPS[roleKey];
  return PAGE_COACH_STEPS[page] || [];
}

export default PAGE_COACH_STEPS;
